import { MATURITY_COLORS } from "@shared/scoring";

// The five maturity stages (the 1–5 readiness scale), colour-coded to match the
// radar's "Readiness Scale" legend. Wording is reproduced exactly as supplied.
type Stage = { name: string; arrow: boolean; points: string[] };

const STAGES: Stage[] = [
  {
    name: "Ideating",
    arrow: true,
    points: [
      "The organization is beginning to explore how AI will impact its people, culture, and structure, including early considerations for agentic workforce models and AI-enabled change.",
      "Workforce data is fragmented, and there is limited visibility into current capabilities, readiness, or learning needs.",
      "HR and organizational functions operate in silos, with minimal alignment to AI transformation goals.",
    ],
  },
  {
    name: "Experimenting",
    arrow: true,
    points: [
      "AI pilot outcomes are informing early workforce planning, learning, and change strategies.",
      "Initial dashboards, AI-enabled tools, and engagement frameworks are being tested to support talent, leadership, and employee experience.",
      "Scenario planning, sentiment analysis, and structural assessments are underway to evaluate future-state needs, including the evolution of agentic roles.",
    ],
  },
  {
    name: "Optimizing",
    arrow: true,
    points: [
      "Formal strategies are in place for workforce planning, learning, change, and HR transformation—aligned with both AI transformation and AI-enabled operations.",
      "Data, systems, and processes are being harmonized to support agility, performance, and decision-making.",
      "Predictive analytics, job architecture, and performance-linked rewards are used to inform talent planning, with consideration for agentic workforce design.",
    ],
  },
  {
    name: "Transforming",
    arrow: true,
    points: [
      "People, culture, and structure are actively enabling AI transformation across the enterprise.",
      "AI-enabled platforms deliver personalized learning, workforce insights, and consumer-grade employee experiences.",
      "Organizational models, HR services, and leadership behaviors are aligned with strategic AI goals and continuously refined.",
    ],
  },
  {
    name: "Leading",
    arrow: false,
    points: [
      "Leadership, culture, and HR are fully integrated into AI strategy—delivering measurable business value and sustained transformation through AI-enabled and agentic approaches.",
      "People data, predictive analytics, and automation drive continuous innovation, agility, and strategic workforce decisions.",
      "The organization has built a future-ready, AI-empowered workforce and operating model.",
    ],
  },
];

export function MaturityStages() {
  return (
    <div className="maturity-scale">
      <div className="maturity-scale-head">
        <h3 className="maturity-scale-title">What each maturity stage looks like</h3>
        <p className="maturity-scale-sub">How readiness shows up as organizations move along the 1–5 scale.</p>
      </div>
      <div className="maturity-grid">
        {STAGES.map((stage, i) => {
          const color = MATURITY_COLORS[i] ?? "var(--violet)";
          return (
            <div className="stage-col" key={stage.name} style={{ ["--accent" as string]: color }}>
              <div className="stage-head" style={{ color }}>
                <span className="stage-rank" style={{ background: color }}>{i + 1}</span>
                <span className="stage-name">{stage.name}{stage.arrow ? " →" : ""}</span>
              </div>
              <ul className="stage-points">
                {stage.points.map((p, j) => (
                  <li key={j}>{p}</li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}
